"use client";

import LinkNavigationBridge from "@/components/LinkNavigationBridge";
import LargePasteImportModal from "@/components/tailwind/import/LargePasteImportModal";
import SearchModal from "@/components/tailwind/search/SearchModal";
import SettingsModal from "@/components/tailwind/settings/modals/SettingsModal";
import GlobalTooltip from "@/components/tailwind/ui/GlobalTooltip";
import { NoteProvider } from "@/contexts/NoteContext";
import { GlobalBlockProvider } from "@/contexts/blockContext";
import { BoardProvider } from "@/contexts/boardContext";
import { BookmarkModalProvider } from "@/contexts/bookmarkModalContext";
import { HtmlUploadModalProvider } from "@/contexts/htmlUploadModalContext";
import { DragStateProvider } from "@/contexts/dragStateContext";
import { EmbedModalProvider } from "@/contexts/embedModalContext";
import { MarketplaceProvider } from "@/contexts/marketplaceContext";
import { MediaLinkModalProvider } from "@/contexts/mediaLinkModalContext";
import { NotificationProvider } from "@/contexts/notification/notificationContext";
import { NotificationSocketListener } from "@/contexts/notification/notificationSocketListner";
import { RootPagesOrderProvider } from "@/contexts/rootPagesOrderContext";
import { SearchProvider } from "@/contexts/searchContext";
import { SettingsModalProvider } from "@/contexts/settingsModalContext";
import { SocketProvider } from "@/contexts/socketContext";
import { WorkAreaProvider } from "@/contexts/workAreaContext";
import { WorkspaceProvider } from "@/contexts/workspaceContext";
import { AgentProvider } from "@/custom-agents/context/AgentContext";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import useLocalStorage from "@/hooks/use-local-storage";
import { UserTimeZoneProvider } from "@/hooks/use-user-timezone";
import { API_URL } from "@/lib/config";
import { installGlobalHandlers } from "@/lib/observability";
import { queryClient } from "@/lib/react-query";
import { GlobalShortcuts } from "@/lib/shortcuts/GlobalShortcuts";
import { ShortcutProvider } from "@/lib/shortcuts/ShortcutProvider";
import { ImageLightboxProvider } from "@/contexts/imageLightboxContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Analytics } from "@vercel/analytics/react";
import { ThemeProvider, useTheme } from "next-themes";
import { usePathname } from "next/navigation";
import { type Dispatch, type ReactNode, type SetStateAction, createContext, useEffect } from "react";
import { Toaster } from "sonner";

export const AppContext = createContext<{
  font: string;
  setFont: Dispatch<SetStateAction<string>>;
}>({
  font: "Default",
  setFont: () => {},
});

function ToasterProvider() {
  const { theme } = useTheme();
  return <Toaster theme={theme as "light" | "dark"} position="bottom-right" />;
}

// This component can use useAuth because it's inside AuthProvider
function AppContent({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.email || "guest";

  return (
    <SocketProvider userId={userId}>
      <WorkspaceProvider>
        <WorkAreaProvider>
          <RootPagesOrderProvider>
            <GlobalBlockProvider>
              <DragStateProvider>
                {/* <BlockTypeRegistryProvider> */}
                <NotificationProvider>
                  <AgentProvider>
                    <NoteProvider>
                      {/* Mounted ONCE app-wide. Do not add per-layout copies —
                          each instance registers its own client-ping handler,
                          so duplicates multiply every invalidation refetch. */}
                      <NotificationSocketListener />
                      <BoardProvider>
                        <EmbedModalProvider>
                          <BookmarkModalProvider>
                          <HtmlUploadModalProvider>
                            <MediaLinkModalProvider>
                              <SettingsModalProvider>
                                <SearchProvider>
                                  <MarketplaceProvider>
                                    {/* <Toaster/> is always mounted now. Severity
                                  gating lives in "@/lib/toast": error/warning
                                  always render; info/success stay dev-only. */}
                                    <ToasterProvider />
                                    <LinkNavigationBridge />
                                    <Analytics />
                                    <SettingsModal />
                                    <SearchModal />
                                    <LargePasteImportModal />
                                    <div
                                      style={{
                                        fontFamily:
                                          'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',
                                      }}
                                    >
                                      {children}
                                    </div>
                                    <ReactQueryDevtools initialIsOpen={false} />
                                  </MarketplaceProvider>
                                </SearchProvider>
                              </SettingsModalProvider>
                            </MediaLinkModalProvider>
                          </HtmlUploadModalProvider>
                          </BookmarkModalProvider>
                        </EmbedModalProvider>
                      </BoardProvider>
                    </NoteProvider>
                  </AgentProvider>
                </NotificationProvider>
                {/* </BlockTypeRegistryProvider> */}
              </DragStateProvider>
            </GlobalBlockProvider>
          </RootPagesOrderProvider>
        </WorkAreaProvider>
      </WorkspaceProvider>
    </SocketProvider>
  );
}

export default function Providers({ children }: { children: ReactNode }) {
  const [font, setFont] = useLocalStorage<string>("novel__font", "Default");

  useEffect(() => {
    // Capture a properly-bound reference to the native fetch. Using
    // `.call(window, ...)` on a detached native method is fragile under
    // Next.js HMR when another module has already patched window.fetch —
    // a bound wrapper ignores the explicit `this` and some bundles throw
    // "Illegal invocation". Binding once here side-steps that whole class
    // of error and lets us invoke `originalFetch(...)` as an ordinary call.
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const isApiCall =
        typeof input === "string" &&
        input.startsWith("/api/") &&
        !input.startsWith("/api/auth") &&
        !input.startsWith("/api/generate");
      if (!isApiCall) return originalFetch(input, init);

      // Rewrite /api/* to the backend URL (e.g. http://localhost:3002/api/*).
      const url = `${API_URL}${input}`;

      // SECURITY (CRIT-01 / CRIT-08): identity flows exclusively via the
      // NextAuth session cookie. We do NOT read localStorage.auth_user and
      // forward it as x-user-email — that header is no longer trusted by
      // the backend and was a full account-impersonation primitive.
      // credentials: "include" makes the browser ship the cookie.
      return originalFetch(url, { ...init, credentials: "include" });
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Install global handlers for the error classes React boundaries can't catch
  // (unhandled promise rejections, event-handler/timer throws). Runs once.
  useEffect(() => {
    installGlobalHandlers();
  }, []);

  // Register the service worker for offline support (runs once on mount)
  //useServiceWorker();

  // Screenshot frames must look the same in every browser, so the capture
  // route pins light mode without touching the viewer's stored preference.
  const pathname = usePathname();
  const forcedTheme = pathname?.startsWith("/capture/") ? "light" : undefined;

  return (
    <ThemeProvider
      attribute="class"
      enableSystem
      disableTransitionOnChange
      defaultTheme="system"
      forcedTheme={forcedTheme}
    >
      <AppContext.Provider
        value={{
          font,
          setFont,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            {/* One fetch of the user's display timezone for the whole app. */}
            <UserTimeZoneProvider>
              <GlobalTooltip />
              {/* Owns the single app-wide keydown listener. Mounted once, above
                everything, so no feature needs a listener of its own. */}
              <ShortcutProvider>
                {/* Bindings whose action needs hooks (theme, router). */}
                <GlobalShortcuts />
                {/* Click-to-open image viewer, shared by the editor and boards. */}
                <ImageLightboxProvider>
                  <AppContent>{children}</AppContent>
                </ImageLightboxProvider>
              </ShortcutProvider>
            </UserTimeZoneProvider>
          </AuthProvider>
        </QueryClientProvider>
      </AppContext.Provider>
    </ThemeProvider>
  );
}
